// src/components/LoginOverlay.js
import React, { useEffect, useRef, useState } from 'react';

/** Minimal markdown -> HTML converter */
function mdToHtml(md) {
  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  let html = esc(md);

  // Code blocks ```...```
  html = html.replace(/```([\s\S]*?)```/g, (m, code) => `<pre><code>${code.replace(/</g, '&lt;')}</code></pre>`);

  // Horizontal rules
  html = html.replace(/^\s*---+\s*$/gm, '<hr/>');

  // Headings
  html = html.replace(/^###### (.*)$/gm, '<h6>$1</h6>');
  html = html.replace(/^##### (.*)$/gm, '<h5>$1</h5>');
  html = html.replace(/^#### (.*)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.*)$/gm, '<h3 class="md-center">$1</h3>');
  html = html.replace(/^## (.*)$/gm, '<h2 class="md-center">$1</h2>');
  html = html.replace(/^# (.*)$/gm, '<h1 class="md-center">$1</h1>');

  // Bold **text**
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Inline code `code`
  html = html.replace(/`([^`]+?)`/g, '<code>$1</code>');

  // Links [text](url)
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Lists
  const lines = html.split('\n');
  let out = [];
  let inList = false;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (/^\s*[-*]\s+/.test(l)) {
      if (!inList) {
        inList = true;
        out.push('<ul>');
      }
      out.push('<li>' + l.replace(/^\s*[-*]\s+/, '') + '</li>');
    } else {
      if (inList) {
        out.push('</ul>');
        inList = false;
      }
      out.push(l);
    }
  }
  if (inList) out.push('</ul>');
  html = out.join('\n');

  // Paragraphs
  html = html
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return '';
      if (/^<(h\d|ul|li|hr|pre|blockquote|p)/.test(trimmed)) return line;
      return `<p>${trimmed}</p>`;
    })
    .join('\n');

  return html;
}

function TermsOverlay({ email, version = '1.2.0', onAgree, onCancel }) {
  const [mdText, setMdText] = useState('Loading Terms of Use…');
  const [enabled, setEnabled] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    fetch('/legal/terms', { cache: 'no-store' })
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load Terms');
        return r.text();
      })
      .then(setMdText)
      .catch(() => setMdText('Failed to load Terms of Use. Please try again later.'));
  }, []);

  const handleScroll = () => {
    const el = boxRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
    if (atBottom) setEnabled(true);
  };

  const handleAgreeClick = async () => {
    try {
      const res = await fetch(`/users/tos-accept?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error('Accept failed');
      onAgree?.(data);
    } catch (err) {
      console.error('tos-accept error', err);
      alert('Failed to record acceptance. Please try again.');
    }
  };

  const html = mdToHtml(mdText);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        style={{
          width: '94vw',
          maxWidth: 820,
          maxHeight: '92vh',
          background: '#ffffff',
          color: '#0f172a',
          borderRadius: 14,
          boxShadow: '0 24px 72px rgba(0,0,0,0.35)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
          <h2 style={{ margin: 0 }}>
            Terms of Use <span style={{ opacity: 0.7 }}>(v{version})</span>
          </h2>
        </div>

        <div
          ref={boxRef}
          onScroll={handleScroll}
          style={{
            padding: '20px 24px',
            overflow: 'auto',
            height: '60vh',
            background: '#f8fafc',
          }}
        >
          <article
            style={{
              maxWidth: 720,
              width: '100%',
              lineHeight: 1.6,
              fontSize: 16,
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>

        <style>
          {`
            .md-center { text-align: center; margin-top: 1.5em; margin-bottom: 0.6em; }
            article h2 { font-size: 1.4em; }
            article h3 { font-size: 1.2em; }
          `}
        </style>

        <div style={{ padding: 16, display: 'flex', gap: 10, justifyContent: 'center', borderTop: '1px solid #e5e7eb' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 16px',
              background: '#e5e7eb',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            disabled={!enabled}
            onClick={handleAgreeClick}
            title={!enabled ? 'Scroll to the bottom to enable' : ''}
            style={{
              padding: '10px 18px',
              background: enabled ? '#1d4ed8' : '#93c5fd',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: enabled ? 'pointer' : 'not-allowed',
            }}
          >
            I Agree
          </button>
        </div>
        <div style={{ padding: '0 16px 14px 16px', fontSize: 12, color: '#64748b', textAlign: 'center' }}>
          You must scroll to the bottom to enable the “I Agree” button.
        </div>
      </div>
    </div>
  );
}

/** Preload an image; resolves true if it loads, false if it errors */
function preloadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

function withCacheBuster(path) {
  const url = new URL(path, window.location.origin);
  url.searchParams.set('ts', String(Date.now()));
  return url.toString();
}

function Spinner({ size = 14 }) {
  const px = typeof size === 'number' ? `${size}px` : size;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <span
        style={{
          width: px,
          height: px,
          borderRadius: '50%',
          border: '2px solid rgba(15, 23, 42, 0.25)',
          borderTopColor: 'rgba(15, 23, 42, 0.95)',
          animation: 'spin 0.8s linear infinite',
          display: 'inline-block',
        }}
      />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </span>
  );
}

// WebCrypto (fast) when available; pure-JS fallback when not in secure context.
async function sha256Hex(str) {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle && typeof TextEncoder !== 'undefined') {
      const enc = new TextEncoder().encode(str);
      const buf = await crypto.subtle.digest('SHA-256', enc);
      const arr = Array.from(new Uint8Array(buf));
      return arr.map((b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    // fall through to JS fallback
  }
  return sha256HexFallback(str);
}

// Minimal SHA-256 fallback (pure JS).
function sha256HexFallback(ascii) {
  function rightRotate(value, amount) {
    return (value >>> amount) | (value << (32 - amount));
  }

  const mathPow = Math.pow;
  const maxWord = mathPow(2, 32);
  let result = '';

  const words = [];
  const asciiBitLength = ascii.length * 8;

  let hash = sha256HexFallback.h || [];
  let k = sha256HexFallback.k || [];
  let primeCounter = k.length;

  const isComposite = {};
  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (!isComposite[candidate]) {
      for (let i = 0; i < 313; i += candidate) isComposite[i] = candidate;
      hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
      k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
    }
  }
  sha256HexFallback.h = hash;
  sha256HexFallback.k = k;

  ascii += '\x80';
  while (ascii.length % 64 - 56) ascii += '\x00';

  for (let i = 0; i < ascii.length; i++) {
    const j = ascii.charCodeAt(i);
    words[i >> 2] |= j << ((3 - i) % 4) * 8;
  }
  words[words.length] = (asciiBitLength / maxWord) | 0;
  words[words.length] = asciiBitLength;

  for (let j = 0; j < words.length; ) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash.slice(0);

    for (let i = 0; i < 64; i++) {
      const w15 = w[i - 15],
        w2 = w[i - 2];

      const a = hash[0],
        e = hash[4];
      const temp1 =
        (hash[7] +
          (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) +
          ((e & hash[5]) ^ (~e & hash[6])) +
          k[i] +
          (w[i] =
            i < 16
              ? w[i]
              : (w[i - 16] +
                  (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) +
                  w[i - 7] +
                  (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) |
                0)) |
        0;

      const temp2 =
        ((rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) +
          ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]))) |
        0;

      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
      hash.pop();
    }

    for (let i = 0; i < 8; i++) hash[i] = (hash[i] + oldHash[i]) | 0;
  }

  for (let i = 0; i < 8; i++) {
    for (let j = 3; j + 1; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }
  return result;
}

/**
 * Solve PoW:
 * find nonce such that sha256(`${salt}:${nonce}`) starts with N zeros.
 * Yields periodically to keep UI responsive.
 */
async function solvePow({ salt, difficulty, abortRef }) {
  const target = '0'.repeat(Math.max(0, Number(difficulty) || 0));
  let i = 0;

  while (true) {
    if (abortRef.current) throw new Error('aborted');

    const nonce = String(i);
    const digest = await sha256Hex(`${salt}:${nonce}`);
    if (digest.startsWith(target)) return nonce;

    i += 1;

    if (i % 250 === 0) {
      await new Promise((r) => setTimeout(r, 0));
    }
  }
}

function CreatePasswordOverlay({ email, antiBotToken, onSuccess, onCancel }) {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const validate = () => {
    if (!pw1 || !pw2) return 'Please fill both password fields.';
    if (pw1.length < 8) return 'Password must be at least 8 characters.';
    if (pw1 !== pw2) return 'Passwords do not match.';
    return '';
  };

  const submit = async () => {
    setErr('');
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }

    setBusy(true);
    try {
      const res = await fetch('/auth/set-password/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(antiBotToken ? { 'X-AntiBot-Token': antiBotToken } : {}),
        },
        body: JSON.stringify({
          email,
          password: pw1,
          company: '', // honeypot
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        const msg = data?.detail || (res.status === 400 ? 'Invalid password.' : 'Failed to set password.');
        throw new Error(msg);
      }

      onSuccess?.();
    } catch (e) {
      console.error('set-password error', e);
      setErr(e?.message || 'Failed to set password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const buttonEnabled = !busy && !!pw1 && !!pw2;

  const eyeBtnStyle = {
    width: 44,
    height: 38,
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    background: '#f8fafc',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: '16px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000001,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        style={{
          width: '92vw',
          maxWidth: 380,
          background: '#ffffff',
          color: '#0f172a',
          borderRadius: 12,
          boxShadow: '0 24px 72px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          padding: 18,
          textAlign: 'center',
        }}
      >
        <h3 style={{ margin: '6px 0 10px 0' }}>Create Password</h3>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>{email}</div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <input
            type={showPw1 ? 'text' : 'password'}
            value={pw1}
            placeholder="Create Password"
            onChange={(e) => setPw1(e.target.value)}
            disabled={busy}
            style={{
              flex: 1,
              padding: '0.55rem',
              textAlign: 'center',
              border: '1px solid #ccc',
              borderRadius: 6,
              boxSizing: 'border-box',
              background: busy ? '#f1f5f9' : '#fff',
            }}
          />
          <button
            type="button"
            onClick={() => setShowPw1((v) => !v)}
            aria-label={showPw1 ? 'Hide password' : 'Show password'}
            title={showPw1 ? 'Hide password' : 'Show password'}
            style={eyeBtnStyle}
          >
            {showPw1 ? '🙈' : '👁️'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <input
            type={showPw2 ? 'text' : 'password'}
            value={pw2}
            placeholder="Confirm Password"
            onChange={(e) => setPw2(e.target.value)}
            disabled={busy}
            style={{
              flex: 1,
              padding: '0.55rem',
              textAlign: 'center',
              border: '1px solid #ccc',
              borderRadius: 6,
              boxSizing: 'border-box',
              background: busy ? '#f1f5f9' : '#fff',
            }}
          />
          <button
            type="button"
            onClick={() => setShowPw2((v) => !v)}
            aria-label={showPw2 ? 'Hide password' : 'Show password'}
            title={showPw2 ? 'Hide password' : 'Show password'}
            style={eyeBtnStyle}
          >
            {showPw2 ? '🙈' : '👁️'}
          </button>
        </div>

        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>Minimum 8 characters. Keep it unique.</div>

        {err && <div style={{ color: 'red', fontSize: 13, margin: '8px 0 10px 0' }}>{err}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 6 }}>
          <button
            onClick={() => {
              setErr('');
              setPw1('');
              setPw2('');
              setShowPw1(false);
              setShowPw2(false);
              onCancel?.();
            }}
            disabled={busy}
            style={{
              padding: '10px 14px',
              background: '#e5e7eb',
              border: 'none',
              borderRadius: 8,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>

          <button
            onClick={submit}
            disabled={!buttonEnabled}
            style={{
              padding: '10px 16px',
              background: buttonEnabled ? '#1d4ed8' : '#93c5fd',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: buttonEnabled ? 'pointer' : 'not-allowed',
              minWidth: 140,
            }}
          >
            {busy ? 'Saving…' : 'Save Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ForgotPasswordOverlay({ email, onClose }) {
  const to = 'wabushinator@gmail.com';
  const subject = `Password Reset Request - ${email || 'User'}`;
  const bodyLines = [
    'Hello Wabushinator Support,',
    '',
    'I would like to reset my password.',
    '',
    `Account email: ${email || ''}`,
    '',
    'Thank you,',
  ];
  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`;

  const tryOpenMailto = async (e) => {
    try {
      e.preventDefault();
      window.location.href = mailto;

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(mailto);
          alert('If your mail app did not open, the email template link was copied to your clipboard.');
        } else {
          alert('If your mail app did not open, please email wabushinator@gmail.com to request a reset.');
        }
      } catch {
        alert('If your mail app did not open, please email wabushinator@gmail.com to request a reset.');
      }
    } catch {
      window.location.href = mailto;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000002,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        style={{
          width: '92vw',
          maxWidth: 420,
          background: '#ffffff',
          color: '#0f172a',
          borderRadius: 12,
          boxShadow: '0 24px 72px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          padding: 18,
          textAlign: 'center',
        }}
      >
        <h3 style={{ margin: '6px 0 10px 0' }}>Forgot Password</h3>

        <div style={{ fontSize: 14, lineHeight: 1.5, color: '#0f172a', marginTop: 8 }}>
          To reset your password, please send an email to{' '}
          <a
            href={mailto}
            onClick={tryOpenMailto}
            style={{ color: '#1d4ed8', textDecoration: 'underline', fontWeight: 800 }}
          >
            {to}
          </a>
          .<br />
          Until official release, password reset will be handled manually and may take a while to complete.
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 16px',
              background: '#e5e7eb',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SVG animation speed control (works on <object> contentDocument)
───────────────────────────────────────────────────────────────────────────── */

function parseSeconds(s) {
  const t = String(s).trim();
  if (!t) return null;
  if (t.endsWith('ms')) {
    const n = parseFloat(t.slice(0, -2));
    return Number.isFinite(n) ? n / 1000 : null;
  }
  if (t.endsWith('s')) {
    const n = parseFloat(t.slice(0, -1));
    return Number.isFinite(n) ? n : null;
  }
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}

function formatSeconds(sec) {
  const v = Number(sec);
  if (!Number.isFinite(v)) return null;
  const s = v.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  return `${s}s`;
}

function scaleCssAnimationDurationsInSvgDoc(svgDoc, speed) {
  const svg = svgDoc?.querySelector?.('svg');
  if (!svg) return;

  const all = svg.querySelectorAll('*');
  all.forEach((el) => {
    // In the SVG document, getComputedStyle is reliable
    const cs = svgDoc.defaultView.getComputedStyle(el);
    const durStr = cs.animationDuration;
    if (!durStr || durStr === '0s') return;

    const parts = durStr.split(',').map((p) => p.trim());
    const scaled = parts.map((p) => {
      const sec = parseSeconds(p);
      if (!sec || sec <= 0) return p;
      return formatSeconds(sec / speed) || p;
    });

    el.style.animationDuration = scaled.join(', ');
  });

  // Force-restart CSS animations so duration changes take effect immediately
  // by toggling a style attribute and forcing reflow.
  svg.style.animation = 'none';
  // eslint-disable-next-line no-unused-expressions
  svg.getBoundingClientRect();
  svg.style.animation = '';
}

function scaleSmilDurationsInSvgDoc(svgDoc, speed) {
  const svg = svgDoc?.querySelector?.('svg');
  if (!svg) return;

  const smil = svg.querySelectorAll('animate, animateTransform, animateMotion, set');
  smil.forEach((node) => {
    const dur = node.getAttribute('dur');
    if (!dur || dur === 'indefinite') return;

    const sec = parseSeconds(dur);
    if (!sec || sec <= 0) return;

    node.setAttribute('dur', formatSeconds(sec / speed) || dur);
  });
}

function applySpeedToEmbeddedSvg(svgDoc, speed) {
  const sp = Number(speed);
  if (!Number.isFinite(sp) || sp <= 0) return;
  try {
    scaleCssAnimationDurationsInSvgDoc(svgDoc, sp);
  } catch {}
  try {
    scaleSmilDurationsInSvgDoc(svgDoc, sp);
  } catch {}
}

function LoginOverlay({ onLogin }) {
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  // Start null to avoid flashing: we render only after final URL is chosen
  const [loginImage, setLoginImage] = useState(null);

  const loginObjectRef = useRef(null);

  // Change this:
  // 2.0 = 2× faster, 1.0 = normal, 0.5 = slower
  const ANIM_SPEED = 1.0;

  const [showTerms, setShowTerms] = useState(false);
  const [loginPayload, setLoginPayload] = useState(null);

  // Honeypot (must remain empty)
  const [company, setCompany] = useState('');

  // Anti-bot state
  const [antiBotChecked, setAntiBotChecked] = useState(false);
  const [antiBotBusy, setAntiBotBusy] = useState(false);
  const [antiBotPassed, setAntiBotPassed] = useState(false);
  const [antiBotToken, setAntiBotToken] = useState(null);

  const [challenge, setChallenge] = useState(null); // {challengeId, salt, difficulty, expiresInSec}
  const abortPowRef = useRef(false);

  // Auth flow state
  const [authStage, setAuthStage] = useState('eligibility'); // 'eligibility' | 'login'
  const [showCreatePassword, setShowCreatePassword] = useState(false);

  // Forgot password UX
  const [forgotVisible, setForgotVisible] = useState(false);
  const [showForgotOverlay, setShowForgotOverlay] = useState(false);

  useEffect(() => {
    const init = async () => {
      const monthNum = new Date().getMonth() + 1;

      // Load SVGs (seasonal + default)
      const seasonalBase = `/Login/${monthNum}.svg`;
      const defaultBase = `/Login/Default.svg`;

      const seasonalURL = withCacheBuster(seasonalBase);
      const defaultURL = withCacheBuster(defaultBase);

      const setFinal = (src) => setLoginImage(src);

      try {
        const res = await fetch('/login-mode/', { cache: 'no-store' });
        const data = await res.json();
        const mode = data?.login_mode === 'seasonal' ? 'seasonal' : 'default';

        if (mode === 'seasonal') {
          let seasonalOk = false;
          try {
            const head = await fetch(seasonalURL, { method: 'HEAD', cache: 'no-store' });
            seasonalOk = head.ok ? true : await preloadImage(seasonalURL);
          } catch {
            seasonalOk = await preloadImage(seasonalURL);
          }
          setFinal(seasonalOk ? seasonalURL : defaultURL);
        } else {
          setFinal(defaultURL);
        }
      } catch {
        setFinal(defaultURL);
      }

      // Fetch anti-bot challenge
      try {
        const cRes = await fetch('/anti-bot/challenge', { cache: 'no-store' });
        const cData = await cRes.json();
        if (cRes.ok && cData?.challengeId && cData?.salt) setChallenge(cData);
        else setChallenge(null);
      } catch {
        setChallenge(null);
      }
    };

    init();
  }, []);

  useEffect(() => {
    return () => {
      abortPowRef.current = true;
    };
  }, []);

  const completeLogin = (payload) => onLogin?.(payload);

  const checkTosAndProceed = async (email, basePayload) => {
    try {
      const res = await fetch(`/users/tos-status?email=${encodeURIComponent(email)}`);
      const data = await res.json();

      if (res.ok && data && data.signed === true) {
        completeLogin(basePayload);
      } else {
        setShowTerms(true);
      }
    } catch (e) {
      console.error('tos-status error', e);
      setError('Unable to verify Terms acceptance. Please try again.');
    }
  };

  const refreshChallenge = async () => {
    try {
      const cRes = await fetch('/anti-bot/challenge', { cache: 'no-store' });
      const cData = await cRes.json();
      if (cRes.ok && cData?.challengeId && cData?.salt) {
        setChallenge(cData);
        return cData;
      }
    } catch {}
    setChallenge(null);
    return null;
  };

  const resetAntiBot = () => {
    abortPowRef.current = true;
    abortPowRef.current = false;
    setAntiBotChecked(false);
    setAntiBotBusy(false);
    setAntiBotPassed(false);
    setAntiBotToken(null);

    setEmailInput('');
    setPasswordInput('');
    setShowLoginPassword(false);

    setError('');
    setAuthStage('eligibility');
    setShowCreatePassword(false);

    setForgotVisible(false);
    setShowForgotOverlay(false);
  };

  const handleAntiBotToggle = async (checked) => {
    setError('');

    if (!checked) {
      resetAntiBot();
      return;
    }

    let localChallenge = challenge || (await refreshChallenge());
    if (!localChallenge) {
      setError('Anti-bot system unavailable. Please refresh.');
      return;
    }

    abortPowRef.current = false;
    setAntiBotChecked(true);
    setAntiBotBusy(true);
    setAntiBotPassed(false);
    setAntiBotToken(null);

    try {
      const nonce = await solvePow({
        salt: localChallenge.salt,
        difficulty: localChallenge.difficulty,
        abortRef: abortPowRef,
      });

      if (abortPowRef.current) throw new Error('aborted');

      const vRes = await fetch('/anti-bot/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: localChallenge.challengeId, nonce }),
      });

      const vData = await vRes.json();

      if (!vRes.ok || !vData?.ok || !vData?.token) {
        throw new Error(vData?.detail ? JSON.stringify(vData.detail) : 'Verify failed');
      }

      setAntiBotToken(vData.token);
      setAntiBotBusy(false);
      setAntiBotPassed(true);
    } catch (e) {
      console.error('Anti-bot failed:', e);
      setAntiBotBusy(false);
      setAntiBotPassed(false);
      setAntiBotChecked(false);
      setAntiBotToken(null);
      setError('Anti-bot check failed. Please try again.');
      await refreshChallenge();
    }
  };

  const handleEligibility = async () => {
    setError('');
    setForgotVisible(false);
    setShowForgotOverlay(false);

    if (!antiBotPassed || !antiBotToken) {
      setError('Please complete the Anti-bot check first.');
      return;
    }
    if (!emailInput) return;

    setChecking(true);

    try {
      const res = await fetch(`/auth/eligibility?email=${encodeURIComponent(emailInput)}`, {
        method: 'GET',
        headers: {
          'X-AntiBot-Token': antiBotToken,
        },
        cache: 'no-store',
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || 'Eligibility check failed.');
      }

      if (!data?.eligible) {
        setError('Wabushinator Usage is only available for my Patreon followers!');
        setAuthStage('eligibility');
        setPasswordInput('');
        setShowLoginPassword(false);
        setForgotVisible(false);
        return;
      }

      if (data?.has_password) {
        setAuthStage('login');
      } else {
        setShowCreatePassword(true);
      }
    } catch (e) {
      console.error('eligibility error', e);
      setError(e?.message || 'Eligibility check failed. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleLogin = async () => {
    setError('');
    setShowForgotOverlay(false);

    if (!antiBotPassed || !antiBotToken) {
      setError('Please complete the Anti-bot check first.');
      return;
    }
    if (!emailInput) return;

    if (authStage !== 'login') {
      setError('Please check eligibility first.');
      return;
    }

    if (!passwordInput) {
      setError('Please enter your password.');
      return;
    }

    setChecking(true);

    try {
      const response = await fetch('/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-AntiBot-Token': antiBotToken,
        },
        body: JSON.stringify({
          email: emailInput,
          password: passwordInput,
          company,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.email && data.credits !== undefined && data.tier) {
        const payload = { email: data.email, credits: data.credits, tier: data.tier };
        setLoginPayload(payload);
        await checkTosAndProceed(data.email, payload);
        return;
      }

      if (data?.detail === 'Password not set') {
        setShowCreatePassword(true);
        setForgotVisible(false);
      } else if (data?.detail === 'Invalid credentials') {
        setError('Invalid password.');
        setForgotVisible(true);
      } else {
        setError('Wabushinator Usage is only available for my Patreon followers!');
        setForgotVisible(false);
      }
    } catch (err) {
      console.error(err);
      setError('Login failed. Please try again.');
      setForgotVisible(false);
    } finally {
      setChecking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (authStage === 'login') handleLogin();
      else handleEligibility();
    }
  };

  const onAgreeTerms = () => {
    if (loginPayload) onLogin?.(loginPayload);
    else onLogin?.({ email: emailInput });
    setShowTerms(false);
  };

  const emailEnabled = antiBotPassed && !checking;
  const canCheckEligibility = antiBotPassed && !!emailInput && !checking && authStage === 'eligibility';
  const canLogin = antiBotPassed && !!emailInput && !!passwordInput && !checking && authStage === 'login';

  const loginPwEyeBtnStyle = {
    width: 44,
    height: 38,
    borderRadius: 8,
    border: '0px solid #cbd5e1',
    background: '#f8fafc',
    cursor: antiBotPassed && !checking ? 'pointer' : 'not-allowed',
    fontSize: 16,
    lineHeight: '16px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
  };

  const handleSvgObjectLoad = () => {
    try {
      const obj = loginObjectRef.current;
      const doc = obj?.contentDocument;
      if (!doc) return;

      // Apply speed scaling after load
      applySpeedToEmbeddedSvg(doc, ANIM_SPEED);
    } catch (e) {
      console.warn('SVG speed apply failed', e);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          backgroundColor: 'rgba(255, 255, 255, 1)',
          padding: '2rem',
          borderRadius: '8px',
          width: '90vw',
          maxWidth: '300px',
          boxShadow: '0 0 10px rgba(0,0,0,1)',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        {/* Animated SVG banner (reliable) */}
        {loginImage && (
          <div
            style={{
              width: '100%',
              maxHeight: '30vh',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <object
              ref={loginObjectRef}
              key={loginImage} // force reload when src changes (cache-buster)
              data={loginImage}
              type="image/svg+xml"
              aria-label="Login Banner"
              onLoad={handleSvgObjectLoad}
              style={{ width: '100%', height: '30vh', display: 'block' }}
            />
          </div>
        )}

        {/* Honeypot field: invisible to humans, often filled by bots */}
        <input
          type="text"
          name="company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        />

        {/* Anti-bot checkbox with spiral */}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            margin: '0 auto 0.9rem auto',
            fontSize: 14,
            color: '#0f172a',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={antiBotChecked}
            onChange={(e) => handleAntiBotToggle(e.target.checked)}
            disabled={checking || antiBotBusy}
            style={{ transform: 'scale(1.1)' }}
          />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span>I'm not a bot</span>
            {antiBotBusy ? <Spinner size={14} /> : antiBotPassed ? <span style={{ opacity: 0.85 }}> ✅️</span> : null}
          </span>
        </label>

        <input
          type="email"
          value={emailInput}
          placeholder={antiBotPassed ? 'Enter your Email' : 'Check box to unlock'}
          onChange={(e) => {
            setEmailInput(e.target.value);
            setError('');
            setAuthStage('eligibility');
            setPasswordInput('');
            setShowLoginPassword(false);
            setForgotVisible(false);
            setShowForgotOverlay(false);
          }}
          onKeyDown={handleKeyDown}
          disabled={!emailEnabled}
          style={{
            display: 'block',
            width: '100%',
            margin: '0 auto 0.8rem auto',
            padding: '0.5rem',
            textAlign: 'center',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxSizing: 'border-box',
            backgroundColor: emailEnabled ? '#fff' : '#f1f5f9',
            cursor: emailEnabled ? 'text' : 'not-allowed',
          }}
        />

        {authStage === 'login' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: '0.7rem' }}>
            <input
              type={showLoginPassword ? 'text' : 'password'}
              value={passwordInput}
              placeholder="Enter Password"
              onChange={(e) => {
                setPasswordInput(e.target.value);
                setError('');
              }}
              onKeyDown={handleKeyDown}
              disabled={!antiBotPassed || checking}
              style={{
                flex: 1,
                padding: '0.5rem',
                textAlign: 'center',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxSizing: 'border-box',
                backgroundColor: antiBotPassed && !checking ? '#fff' : '#f1f5f9',
              }}
            />
            <button
              type="button"
              onClick={() => setShowLoginPassword((v) => !v)}
              aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
              title={showLoginPassword ? 'Hide password' : 'Show password'}
              disabled={!antiBotPassed || checking}
              style={loginPwEyeBtnStyle}
            >
              {showLoginPassword ? '🙈' : '🙉️'}
            </button>
          </div>
        )}

        {authStage === 'eligibility' ? (
          <button
            onClick={handleEligibility}
            disabled={!canCheckEligibility}
            style={{
              padding: '0.5rem 1.2rem',
              backgroundColor: canCheckEligibility ? '#1d4ed8' : '#93c5fd',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              display: 'block',
              margin: '0 auto',
              cursor: canCheckEligibility ? 'pointer' : 'not-allowed',
              width: '100%',
            }}
          >
            {checking ? 'Checking…' : 'Check Eligibility'}
          </button>
        ) : (
          <button
            onClick={handleLogin}
            disabled={!canLogin}
            style={{
              padding: '0.5rem 1.2rem',
              backgroundColor: canLogin ? '#1d4ed8' : '#93c5fd',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              display: 'block',
              margin: '0 auto',
              cursor: canLogin ? 'pointer' : 'not-allowed',
              width: '100%',
            }}
          >
            {checking ? 'Checking…' : 'Login'}
          </button>
        )}

        {/* Reveal after wrong password */}
        {authStage === 'login' && forgotVisible && (
          <button
            onClick={() => setShowForgotOverlay(true)}
            style={{
              marginTop: '10px',
              padding: '8px 12px',
              background: '#ff0000ff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '100%',
              color: '#ffffffff',
            }}
          >
            Forgot Password
          </button>
        )}

        {error && <p style={{ color: 'red', marginTop: '1rem', textAlign: 'center' }}>{error}</p>}

        <span
          style={{
            position: 'absolute',
            bottom: '8px',
            right: '12px',
            fontSize: '11px',
            color: '#64748b',
            fontStyle: 'italic',
          }}
        >
          BETA 1.0.2 Production
        </span>
      </div>

      {showTerms && (
        <TermsOverlay email={emailInput} version="1.2.0" onAgree={onAgreeTerms} onCancel={() => setShowTerms(false)} />
      )}

      {showCreatePassword && (
        <CreatePasswordOverlay
          email={emailInput}
          antiBotToken={antiBotToken}
          onCancel={() => {
            setShowCreatePassword(false);
            setAuthStage('eligibility');
            setPasswordInput('');
            setShowLoginPassword(false);
            setForgotVisible(false);
          }}
          onSuccess={async () => {
            setShowCreatePassword(false);
            setAuthStage('login');
            setPasswordInput('');
            setShowLoginPassword(false);
            setError('');
            setForgotVisible(false);
          }}
        />
      )}

      {showForgotOverlay && <ForgotPasswordOverlay email={emailInput} onClose={() => setShowForgotOverlay(false)} />}
    </div>
  );
}

export default LoginOverlay;
